import { FC, MutableRefObject, PropsWithChildren, useCallback, useMemo, useRef } from 'react';
import Webcam from 'react-webcam';

import { CAPTURE_VIDEO_CONSTRAINTS } from '../capture-constraints';

import {
  ContainerDamageLocation,
  ContainerDamageTypes,
  DamageType,
  EquipmentType,
  containerDamageHasLocation,
  containerDamageWithLocationHasPart,
  isContainerDamage
} from 'types/damage';
import { ImageOverlay } from 'utils';

import { useDamage } from '.';
import { useNewTransaction } from '../new-transaction.context';

import containerBottomSVG from 'assets/svgs/Bottom Side Container.svg';
import containerDoorSVG from 'assets/svgs/Door Container.svg';
import containerFrontSVG from 'assets/svgs/Front Container.svg';
import containerLeftSVG from 'assets/svgs/Left Side Container.svg';
import containerRightSVG from 'assets/svgs/Right Side Container.svg';
import containerTopSVG from 'assets/svgs/Top SIde Container.svg';
import { useDamageOptions } from 'contexts/damage-options';

const containerSVGs: { [P in Exclude<ContainerDamageLocation, ContainerDamageLocation.MISC>]: any } = {
  [ContainerDamageLocation.DOOR]: containerDoorSVG,
  [ContainerDamageLocation.FRONT]: containerFrontSVG,
  [ContainerDamageLocation.TOP]: containerTopSVG,
  [ContainerDamageLocation.LEFT]: containerLeftSVG,
  [ContainerDamageLocation.RIGHT]: containerRightSVG,
  [ContainerDamageLocation.BOTTOM]: containerBottomSVG
};

const isContainerDamageLocation = (key: string): key is ContainerDamageLocation => key in ContainerDamageLocation;

const ContainerDialogWrapper: FC<
  PropsWithChildren<{ className?: string; back?: () => void; next?: () => void }>
> = props => {
  return (
    <div>
      <div className="d-flex bg-primary g-3 p-1">
        <button type="button" className="btn-back d-flex g-2" onClick={props.back} disabled={!props.back}>
          <span className="material-icons icon-large">arrow_back</span>
          <span>BACK</span>
        </button>
        <h4 className="flex-grow-1 text-center">CONTAINER DAMAGE SELECTION</h4>
        <button type="button" className="btn-back d-flex g-2" onClick={props.next} disabled={!props.next}>
          <span>NEXT</span>
          <span className="material-icons icon-large">arrow_forward</span>
        </button>
      </div>
      <div className={`dialog-content ${props.className ?? ''} p-5`}>{props.children}</div>
    </div>
  );
};

const DamageImage: FC<{ closeDialog: () => void }> = () => {
  const { current, capture, setCapture, setImage } = useDamage();
  const { container, chassis, genset } = useNewTransaction();
  const webcamRef = useRef<Webcam>(null);

  const handleCapture = useCallback(async () => {
    const text = [container.unitNumber ?? '', chassis.unitNumber ?? ' ', genset.unitNumber ?? ' '].join(' ');
    ImageOverlay.withText(text + ' ' + new Date().toLocaleString())
      .withSetter(setImage)
      .call(Webcam.prototype.getScreenshot.call(webcamRef.current));
  }, [setImage, container.unitNumber, chassis.unitNumber, genset.unitNumber]);

  return !capture ? null : (
    <ContainerDialogWrapper
      className="webcam"
      back={() => setCapture(false)}
      next={!current.imageBase64 ? undefined : () => setCapture(false)}
    >
      {!current.imageBase64 ? (
        <div className="d-flex flex-column">
          <Webcam
            width="100%"
            ref={webcamRef}
            videoConstraints={CAPTURE_VIDEO_CONSTRAINTS}
            screenshotFormat="image/jpeg"
          />
          <button type="button" className="btn-close" onClick={handleCapture}>
            <span className="material-icons">circle</span>
          </button>
        </div>
      ) : (
        <div className="d-flex flex-column">
          <img alt="preview" width="100%" src={current.imageBase64} />
          <button type="button" className="btn-close" onClick={() => setImage(undefined)}>
            <span className="material-icons">rotate_left</span>
          </button>
        </div>
      )}
    </ContainerDialogWrapper>
  );
};

const DamageTypeSelection: FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { current, setCapture, setCurrent, setIndex, submit } = useDamage();
  const { containerDamageMap } = useDamageOptions();

  const damageLocationRef: MutableRefObject<HTMLInputElement | null> = useRef(null);
  const damageTypeRef: MutableRefObject<HTMLSelectElement | null> = useRef(null);

  const handleSubmit = () => {
    if (!current.location) {
      damageLocationRef.current?.setCustomValidity('Damage location required');
      damageLocationRef.current?.reportValidity();
    } else if (!current.damageType) {
      damageTypeRef.current?.setCustomValidity('Damage type required');
      damageTypeRef.current?.reportValidity();
    } else {
      damageLocationRef.current?.setCustomValidity('');
      damageTypeRef.current?.setCustomValidity('');
      submit();
      closeDialog();
    }
  };

  return !isContainerDamage(current) ||
    !containerDamageHasLocation(current) ||
    !containerDamageWithLocationHasPart(current) ? null : (
    <ContainerDialogWrapper back={() => setIndex(1)} className="damage-type">
      <h5 className="mb-5 text-center text-primary">
        CONTAINER | {current.location} | {current.part}
      </h5>
      <div className="grid-3 p-3 auto-rows">
        <div className="damage-input col-span-2">
          <input
            type="text"
            className="w-100"
            defaultValue={current.location}
            disabled
            ref={node => {
              node?.toggleAttribute('blank', !current.location); /* label transform */
              damageLocationRef.current = node;
            }}
          />
          <label>DAMAGE LOCATION *</label>
        </div>
        <div className="row-span-2 h-100 w-100">
          {!current.imageBase64 ? (
            <button
              type="button"
              className="btn-outline justify-content-center h-100 w-100 grid-1 text-primary"
              onClick={() => setCapture(true)}
            >
              <span className="material-icons image">camera_alt</span>
              <span className="white-space-nowrap text-base">TAKE PHOTO</span>
            </button>
          ) : (
            <button
              type="button"
              className="btn-outline justify-content-center text-info-main h-100 w-100 grid-1"
              onClick={() => setCapture(true)}
            >
              <span className="material-icons image">image</span>
              <span className="white-space-nowrap text-base">VIEW PHOTO</span>
            </button>
          )}
        </div>
        <div className="damage-input col-span-2">
          <select
            className="w-100"
            value={current.damageType ?? ''}
            onChange={e => setCurrent(p => ({ ...p, damageType: (e.target.value as DamageType) || undefined }))}
            ref={node => {
              node?.toggleAttribute('blank', !current.damageType); /* label transform */
              damageTypeRef.current = node;
            }}
          >
            <option value="" />
            {Object.assign<DamageType[], DamageType[] | undefined>(
              [],
              containerDamageMap[current.location][current.part]
            ).map(damageType => (
              <option key={damageType} value={damageType}>
                {damageType}
              </option>
            ))}
          </select>
          <label>DAMAGE TYPE *</label>
        </div>
        <div className="damage-input col-span-3 row-span-2 h-100">
          <textarea
            className="h-100 w-100"
            ref={node => node?.toggleAttribute('blank', !current.notes)}
            value={current.notes}
            onChange={e => setCurrent(p => ({ ...p, notes: e.target.value }))}
          />
          <label>CONTAINER REPAIR NOTES</label>
        </div>
        <button className="btn-outline col-start-2 align-self-end cancel" type="button" onClick={closeDialog}>
          CANCEL
        </button>
        <button className="btn-primary col-start-3 align-self-end" type="button" onClick={handleSubmit}>
          SUBMIT
        </button>
      </div>
    </ContainerDialogWrapper>
  );
};

type ContainerPart = keyof ContainerDamageTypes[keyof ContainerDamageTypes];
const DamagePartSelection: FC<{ closeDialog: () => void }> = () => {
  const { current, damages, setCurrent, setIndex } = useDamage();
  const { containerDamageMap } = useDamageOptions();

  const counts = useMemo(() => {
    return damages
      .filter(isContainerDamage)
      .filter(containerDamageHasLocation)
      .filter(containerDamageWithLocationHasPart)
      .filter(damage => damage.location === current.location)
      .reduce((acc: { [key: string]: number }, { part }) => {
        return { ...acc, [part]: (acc[part] ?? 0) + 1 };
      }, {});
  }, [current.location, damages]);

  return !isContainerDamage(current) || !containerDamageHasLocation(current) ? null : (
    <ContainerDialogWrapper
      back={() => setIndex(0)}
      next={containerDamageWithLocationHasPart(current) ? () => setIndex(2) : undefined}
    >
      <h5 className="mb-5 text-center text-primary">CONTAINER | {current.location}</h5>
      <div className="grid-2">
        {Object.assign<ContainerPart[], any[]>([], Object.keys({ ...containerDamageMap[current.location] })).map(
          part => (
            <div key={part} className="badge-container h-100 w-100">
              <button
                type="button"
                className="btn-outline justify-content-center text-info-main g-2 h-100 w-100"
                onClick={() => {
                  setCurrent({ equipmentType: EquipmentType.CONTAINER, location: current.location, part });
                  setIndex(2);
                }}
                ref={node => node?.toggleAttribute('selected', part === current.part)}
              >
                <label className="font-weight-bold text-big"> {part}</label>
              </button>
              {!!counts[part] && <div className="badge background-primary-dark text-md text-white">{counts[part]}</div>}
            </div>
          )
        )}
      </div>
    </ContainerDialogWrapper>
  );
};

const DamageLocationSelection: FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { current, damages, setCurrent, setIndex } = useDamage();

  const counts = useMemo(() => {
    return damages
      .filter(isContainerDamage)
      .filter(containerDamageHasLocation)
      .reduce((acc: { [key: string]: number }, { location }) => {
        return { ...acc, [location]: (acc[location] ?? 0) + 1 };
      }, {});
  }, [damages]);

  return !isContainerDamage(current) ? null : (
    <ContainerDialogWrapper
      back={closeDialog}
      next={containerDamageHasLocation(current) ? () => setIndex(1) : undefined}
    >
      <h5 className="mb-5 text-center text-primary">CONTAINER OVERVIEW</h5>
      <div className="grid-3">
        {Object.keys(containerSVGs)
          .filter(isContainerDamageLocation)
          .map(location => (
            <div key={location} className="badge-container h-100 w-100">
              <svg width="100%" viewBox="0 0 1 1" />
              <div className="p-absolute h-100 w-100">
                <button
                  type="button"
                  className="btn-outline text-info-main g-2 h-100 w-100"
                  onClick={() => {
                    setCurrent({ equipmentType: EquipmentType.CONTAINER, location });
                    setIndex(1);
                  }}
                  ref={node => node?.toggleAttribute('selected', location === current.location)}
                >
                  <img
                    alt={location}
                    src={containerSVGs[location as Exclude<ContainerDamageLocation, ContainerDamageLocation.MISC>]}
                  />
                  <label className="font-weight-bold text-big">{location}</label>
                </button>
                {!!counts[location] && (
                  <div className="badge background-primary-dark text-md text-white">{counts[location]}</div>
                )}
              </div>
            </div>
          ))}
        <div className="badge-container col-span-3 h-100 w-100">
          <svg width="100%" viewBox="0 0 9 1" />
          <div className="p-absolute h-100 w-100">
            <button
              type="button"
              className="btn-outline justify-content-center text-info-main g-2 h-100 w-100"
              onClick={() => {
                setCurrent({ equipmentType: EquipmentType.CONTAINER, location: ContainerDamageLocation.MISC });
                setIndex(1);
              }}
              ref={node => node?.toggleAttribute('selected', current.location === ContainerDamageLocation.MISC)}
            >
              <label className="font-weight-bold text-big">MISC</label>
            </button>
          </div>
        </div>
      </div>
    </ContainerDialogWrapper>
  );
};

const ContainerDialogContent: FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { capture, current, index } = useDamage();

  const Selection: FC<{ closeDialog: () => void }> = useMemo(() => {
    return !isContainerDamage(current)
      ? () => null
      : !containerDamageHasLocation(current) || index === 0
      ? DamageLocationSelection
      : !containerDamageWithLocationHasPart(current) || index === 1
      ? DamagePartSelection
      : !capture
      ? DamageTypeSelection
      : DamageImage;
  }, [capture, current, index]);

  return <Selection closeDialog={closeDialog} />;
};

export default ContainerDialogContent;
